import type { LearnTopic } from "../topic-types";

/**
 * Attachment Styles.
 *
 * Educational and non-diagnostic throughout: patterns to explore, never
 * labels to be placed in. Add new guides, exercises, prompts, worksheets or
 * audio by appending to a section's `resources` array.
 */
export const ATTACHMENT_TOPIC: LearnTopic = {
  slug: "attachment-styles",
  emoji: "💛",
  title: "Attachment Styles",
  tagline: "Understand your patterns of connection",
  theme: "gold",
  category: "relationships",
  cardBlurb:
    "Secure, anxious, avoidant and disorganised patterns — explored as patterns, never labels.",
  continueTo: [
    { label: "Trauma & Relationships", to: "/learn/trauma-relationships" },
    { label: "Boundaries After Trauma", to: "/learn/boundaries" },
    { label: "Trauma Bonds", to: "/learn/trauma-bonds" },
    { label: "Inner Child Work", to: "/learn/inner-child" },
    { label: "Open my journal", to: "/journal" },
  ],
  seoDescription:
    "Explore how patterns of connection, emotional safety and relationships can develop — and how greater awareness can support healthier relationships with yourself and others. Educational, non-diagnostic guides from Recovery Trauma™.",
  intro: [
    "Explore how patterns of connection, emotional safety and relationships can develop — and how greater awareness can support healthier relationships with yourself and others.",
    "This section combines education with practical self-reflection rather than simply describing four attachment styles.",
    "Most people recognise something in more than one pattern, and patterns can differ between relationships and across time. Nothing here is a category you belong to.",
  ],
  closing:
    "Attachment theory is an educational framework, not a diagnosis or a replacement for professional mental health treatment. Relationship patterns rooted in trauma are often best explored alongside a trauma-informed therapist.",
  sections: [
    {
      slug: "the-four-patterns",
      emoji: "🧭",
      title: "Explore the four attachment patterns",
      blurb: "Four learning paths, written as patterns to notice rather than labels.",
      intro:
        "You may recognise parts of several of these. Read them as descriptions of what can happen in connection, not as a description of who you are.",
      resources: [
        {
          slug: "secure",
          kind: "learn",
          title: "🤍 Secure attachment",
          summary: "What emotional safety in connection can look and feel like.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Secure patterns are often described as being able to be close without losing yourself, and separate without losing the relationship. It is less a personality type than a felt sense that connection is broadly survivable.",
            },
            {
              type: "list",
              title: "Some people notice",
              items: [
                "Emotional safety — being able to say something difficult and stay in the room",
                "Healthy independence alongside genuine closeness",
                "Communication that names a need rather than testing for it",
                "Trust that builds gradually and updates with evidence",
                "Boundaries held without a great deal of guilt",
                "Repair after conflict — returning, rather than disappearing",
              ],
            },
            {
              type: "note",
              text: "Security is not a fixed trait or a finish line. Many people describe it as something they experience more often in some relationships than in others.",
            },
          ],
        },
        {
          slug: "anxious",
          kind: "learn",
          title: "💛 Anxious attachment",
          summary: "When closeness matters deeply and distance feels alarming.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Anxious patterns often develop where care was real but unpredictable. Staying alert to small shifts in connection made sense: it was how closeness was kept.",
            },
            {
              type: "list",
              title: "This pattern can sometimes involve",
              items: [
                "Fear of abandonment that arrives before there is evidence",
                "Seeking reassurance, and finding it soothes only briefly",
                "Relationship anxiety that rises in silences and delays",
                "Hypervigilance to changes in tone, timing or warmth",
                "Emotional activation that feels bigger than the trigger",
                "Difficulty settling until the connection feels confirmed",
              ],
            },
            {
              type: "prose",
              text: "Understanding triggers, and building internal security so that reassurance is not the only source of steadiness, is the focus of the Building Secure Attachment section.",
            },
          ],
        },
        {
          slug: "avoidant",
          kind: "learn",
          title: "💙 Avoidant attachment",
          summary: "When independence feels safer than depending on someone.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Avoidant patterns often develop where needs were met with distance, irritation or absence. Self-reliance was not coldness — it was the option available.",
            },
            {
              type: "list",
              title: "You may recognise",
              items: [
                "Emotional distancing when a conversation gets close",
                "Strong independence, and pride in not needing much",
                "Difficulty relying on others even when support is offered",
                "Discomfort with vulnerability, or a blank feeling around it",
                "Pulling away during conflict rather than moving toward it",
                "Deactivating strategies — noticing flaws, needing space, going quiet",
              ],
            },
            {
              type: "note",
              text: "People with avoidant patterns often care a great deal. The distance is frequently a protective habit rather than an absence of feeling.",
            },
          ],
        },
        {
          slug: "disorganised",
          kind: "learn",
          title: "💜 Disorganised attachment",
          summary: "When the wish for closeness and the fear of it arrive together.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Disorganised patterns are often described where the same person was both the source of comfort and the source of fear. Two responses that both make sense — move toward, move away — can then arrive at once.",
            },
            {
              type: "list",
              title: "This pattern can sometimes involve",
              items: [
                "Wanting closeness while fearing it in the same moment",
                "Push-pull patterns that can be confusing from the inside too",
                "Difficulty trusting that safety will hold",
                "Emotional overwhelm that arrives quickly",
                "Conflicting responses to the same situation",
                "A sense that connection and danger are hard to tell apart",
              ],
            },
            {
              type: "note",
              text: "This is often the pattern most closely linked with trauma, and the one where working with a trauma-informed therapist tends to help most. Nothing here is a substitute for that support.",
            },
          ],
        },
      ],
    },
    {
      slug: "understand-your-patterns",
      emoji: "🔎",
      title: "Understand your patterns",
      blurb: "Guided questions for noticing how connection tends to feel for you.",
      intro:
        "These questions are for curiosity, not conclusions. There is no score and no result.",
      resources: [
        {
          slug: "understand-my-attachment-patterns",
          kind: "reflect",
          title: "Understand my attachment patterns",
          summary: "Six questions about closeness, distance and conflict.",
          minutes: 10,
          blocks: [
            {
              type: "prompts",
              items: [
                "What happens in me when someone feels distant?",
                "What happens when conflict occurs — do I move toward, away, or both?",
                "How comfortable does closeness feel, in the body as well as the mind?",
                "Which situations tend to trigger relationship anxiety?",
                "What makes connection feel unsafe?",
                "What helps connection feel safer?",
              ],
            },
            {
              type: "note",
              text: "Answers often differ depending on who the relationship is with. Noticing that difference is useful information in itself.",
            },
          ],
        },
        {
          slug: "patterns-across-relationships",
          kind: "reflect",
          title: "Patterns across relationships",
          summary: "How the same person can respond differently in different bonds.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "Where do I feel most myself in connection?",
                "Where do I feel most on guard?",
                "What is different about those two relationships?",
                "Which of my responses show up everywhere, and which are specific?",
                "What would I like more of in connection?",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "relationship-triggers",
      emoji: "⚡",
      title: "Relationship triggers",
      blurb: "Track a moment from situation through to what was needed.",
      intro:
        "Working backwards through a single moment often reveals more than analysing a whole relationship.",
      resources: [
        {
          slug: "my-relationship-triggers",
          kind: "practice",
          title: "My relationship triggers",
          summary: "A six-step chain for one specific moment.",
          minutes: 12,
          blocks: [
            {
              type: "prose",
              text: "Choose one recent moment — small is better than significant while you are learning the pattern.",
            },
            {
              type: "flow",
              items: [
                "Situation",
                "What I noticed",
                "Emotion",
                "Automatic reaction",
                "What I feared",
                "What I needed",
              ],
            },
            {
              type: "prose",
              text: "Over time, many people find the last two lines repeat. That repetition is usually the more useful information.",
            },
          ],
        },
        {
          slug: "when-connection-feels-unsafe",
          kind: "reflect",
          title: "When connection feels unsafe",
          summary: "Prompts for the moments closeness turns into alarm.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "What was the first signal — in the body, before the thought?",
                "What story arrived with it?",
                "How old did that feeling seem?",
                "What did I do next, and what was that protecting?",
                "What would have helped, if it had been available?",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "building-secure-attachment",
      emoji: "🌱",
      title: "Building secure attachment",
      blurb: "Practical recovery, not only identifying patterns.",
      intro:
        "Security tends to be built in small, repeated experiences rather than decided in one conversation.",
      resources: [
        {
          slug: "communicating-needs",
          kind: "learn",
          title: "Communicating needs",
          summary: "Naming a need directly, without testing whether it will be noticed.",
          minutes: 4,
          blocks: [
            {
              type: "steps",
              title: "One structure people find manageable",
              items: [
                { label: "The situation", note: "Describe it plainly, without interpretation." },
                { label: "What I felt", note: "One word is enough." },
                { label: "What I need", note: "Specific and small enough to be possible." },
                { label: "The invitation", note: "Ask, rather than require agreement immediately." },
              ],
            },
            {
              type: "note",
              text: "Asking directly can feel exposing when indirect asking kept you safe for years. Feeling awkward is not a sign it is going wrong.",
            },
          ],
        },
        {
          slug: "setting-boundaries",
          kind: "learn",
          title: "Setting boundaries",
          summary: "Boundaries as information about you, not punishment of someone else.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Some ideas",
              items: [
                "A boundary describes what you will do, not what another person must do",
                "It can be said warmly; warmth does not weaken it",
                "Guilt after a boundary is common and does not mean it was wrong",
                "Boundaries can be adjusted as trust builds",
              ],
            },
            {
              type: "prompts",
              title: "Reflection",
              items: [
                "Where do I most often say yes when I mean no?",
                "What am I afraid would happen if I said no there?",
                "What is one boundary that would change my week?",
              ],
            },
          ],
        },
        {
          slug: "tolerating-healthy-space",
          kind: "practice",
          title: "Tolerating healthy space",
          summary: "Staying with yourself when someone is temporarily unavailable.",
          minutes: 8,
          blocks: [
            {
              type: "steps",
              items: [
                { label: "Notice the alarm", note: "Name it: something in me is frightened by this gap." },
                { label: "Slow the body", note: "A longer out-breath, feet on the floor, cold water." },
                { label: "Check the evidence", note: "What is known, separately from what is feared." },
                { label: "Offer yourself the reassurance", note: "In the words you would want to hear." },
                { label: "Choose the next small thing", note: "Something that is yours, not about the relationship." },
              ],
            },
            {
              type: "note",
              text: "This is a practice for ordinary distance, not for relationships where you feel unsafe. If something is frightening you, that deserves support rather than tolerance.",
            },
          ],
        },
        {
          slug: "recognising-safe-relationships",
          kind: "learn",
          title: "Recognising safe relationships",
          summary: "What safety tends to look like when it is real.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "Some people notice",
              items: [
                "Consistency between what is said and what happens",
                "Space to disagree without the relationship being at risk",
                "Repair being offered, not only accepted",
                "Your no being received without a cost",
                "Feeling more like yourself, not less",
              ],
            },
          ],
        },
        {
          slug: "repair-after-conflict",
          kind: "practice",
          title: "Repair after conflict",
          summary: "Returning to each other after a rupture.",
          minutes: 6,
          blocks: [
            {
              type: "steps",
              items: [
                { label: "Settle first", note: "Repair rarely works while either person is still activated." },
                { label: "Name your part in it", note: "Specific, and without a general confession." },
                { label: "Say what you were protecting", note: "Often the more useful information." },
                { label: "Ask what it was like for them", note: "And let the answer land before responding." },
                { label: "Agree one small thing", note: "For next time — small enough to actually happen." },
              ],
            },
          ],
        },
        {
          slug: "building-internal-safety",
          kind: "reflect",
          title: "Building internal safety",
          summary: "Becoming a steadier place for yourself to return to.",
          minutes: 8,
          blocks: [
            {
              type: "prompts",
              items: [
                "What steadies me that does not depend on anyone else?",
                "How do I speak to myself when connection wobbles?",
                "What would I say to a friend in this exact position?",
                "What would trusting myself slightly more look like this week?",
              ],
            },
            {
              type: "prose",
              text: "Self-soothing is not a replacement for connection. It is what makes connection less frightening to reach for.",
            },
          ],
        },
      ],
    },
    {
      slug: "exercises-and-worksheets",
      emoji: "📄",
      title: "Exercises & worksheets",
      blurb: "Printable pages for reflection away from a screen.",
      intro:
        "Print these, or copy the headings into your journal. Nothing you write is stored on this page.",
      resources: [
        {
          slug: "my-attachment-patterns-worksheet",
          kind: "worksheet",
          title: "My Attachment Patterns",
          summary: "The six core questions, with room to write.",
          worksheetFields: [
            { label: "What happens when someone feels distant", lines: 3 },
            { label: "What happens when conflict occurs", lines: 3 },
            { label: "How closeness feels", lines: 2 },
            { label: "What triggers relationship anxiety", lines: 3 },
            { label: "What makes connection feel unsafe", lines: 2 },
            { label: "What helps connection feel safer", lines: 3 },
          ],
        },
        {
          slug: "relationship-trigger-map",
          kind: "worksheet",
          title: "Relationship Trigger Map",
          summary: "Situation → noticed → emotion → reaction → feared → needed.",
          worksheetFields: [
            { label: "Situation", lines: 2 },
            { label: "What I noticed", lines: 2 },
            { label: "Emotion", lines: 1 },
            { label: "Automatic reaction", lines: 2 },
            { label: "What I feared", lines: 2 },
            { label: "What I needed", lines: 2 },
          ],
        },
        {
          slug: "needs-and-boundaries",
          kind: "worksheet",
          title: "Needs & Boundaries",
          summary: "A page for naming one need and one boundary clearly.",
          worksheetFields: [
            { label: "A need I find hard to say out loud", lines: 2 },
            { label: "How I could say it plainly", lines: 3 },
            { label: "A boundary I would like to hold", lines: 2 },
            { label: "What I expect to feel afterwards", lines: 2 },
            { label: "What would help me hold it anyway", lines: 2 },
          ],
        },
        {
          slug: "repair-after-conflict-worksheet",
          kind: "worksheet",
          title: "Repair After Conflict",
          summary: "Working through one rupture, gently.",
          worksheetFields: [
            { label: "What happened", lines: 3 },
            { label: "What I was protecting", lines: 2 },
            { label: "What I imagine it was like for them", lines: 3 },
            { label: "What I would like to say", lines: 3 },
            { label: "One small thing for next time", lines: 2 },
          ],
        },
        {
          slug: "building-security-tracker",
          kind: "worksheet",
          title: "Building Security — Weekly Tracker",
          summary: "Small repeated experiences, noticed on purpose.",
          worksheetFields: [
            { label: "A moment connection felt safe this week", lines: 3 },
            { label: "Something I asked for directly", lines: 2 },
            { label: "A boundary I held", lines: 2 },
            { label: "A moment I steadied myself", lines: 2 },
            { label: "What I would like more of next week", lines: 2 },
          ],
        },
        {
          slug: "guided-attachment-practice",
          kind: "audio",
          title: "Guided practice: staying with the gap",
          summary: "An audio version of Tolerating Healthy Space, coming to this section.",
          status: "soon",
        },
      ],
    },
  ],
};
